'use client'
import { useCallback, useEffect, useRef, useState } from 'react'
import useWindowSize from '@/src/hooks/useWindowSize'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { useSwipeable } from 'react-swipeable'
import useTimeout from '@/src/hooks/useTimeout'
import usePageLoad from '@/src/hooks/usePageLoad'

export type CarouselDirection = 'prev' | 'next' | 'left' | 'right'

export type CarouselProps = {
  step?: number
  scrollSpeed?: number
  onScrollStart?: gsap.Callback
  onScrollEnd?: gsap.Callback
  circular?: boolean
  autoplay?: {
    direction: CarouselDirection
    speed: number
    interval: number
  }
}

export default function useCarousel<T extends HTMLElement>(config?: CarouselProps) {
  const {
    step = 1,
    scrollSpeed = 500,
    circular = false,
    autoplay,
    onScrollStart,
    onScrollEnd,
  } = config ?? {}

  gsap.registerPlugin(useGSAP) // register the hook to avoid React version discrepancies
  gsap.registerPlugin(ScrollToPlugin)

  const refContainer = useRef<T>(null)

  const hovered = useRef(false)

  const tlRef = useRef(gsap.timeline())

  const [itemPositionIdx, setItemPositionIdx] = useState(0)
  const itemPositionIdxRef = useRef(itemPositionIdx)
  useEffect(() => {
    itemPositionIdxRef.current = itemPositionIdx
  }, [itemPositionIdx])

  const timedAutoplay = useTimeout(
    !autoplay
      ? undefined
      : {
          callback: () => {
            scrollToIdx(autoplay.direction, true, autoplay.speed)
          },
          delay: autoplay.interval,
        },
  )

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => scrollToNextIdx(),
    onSwipedRight: () => scrollToPrevIdx(),
    swipeDuration: Infinity,
    preventScrollOnSwipe: true,
    trackMouse: true,
  })

  const refCarousel = (el: T) => {
    swipeHandlers.ref(el)
    refContainer.current = el
  }

  const calculatePositions = useCallback(() => {
    function leftPos(el: Element) {
      return Math.ceil(
        el.getBoundingClientRect().left - el.parentElement!.getBoundingClientRect().left,
      )
    }

    const container = refContainer.current
    if (!container) return []

    const items = [...container.children]

    const firstChildLeftPos = leftPos(items[0])
    let positions: number[] = []

    for (let i = 0; i < items.length; i++) {
      const childLeftPos = leftPos(items[i])
      const childScrollLeft = childLeftPos - firstChildLeftPos
      positions.push(childScrollLeft)
      if (Math.ceil(childScrollLeft + container.clientWidth) >= Math.ceil(container.scrollWidth))
        break
    }
    positions = [...new Set<number>(positions)]
    return positions
  }, [])

  const scrollToIdx = useCallback(
    (_itemPositionIdx?: number | CarouselDirection, animated = true, speed = scrollSpeed) => {
      const isResizing = _itemPositionIdx === undefined

      if (!_itemPositionIdx) _itemPositionIdx = itemPositionIdxRef.current

      const itemsPositions = calculatePositions()

      const container = refContainer.current
      if (!container) return

      if (_itemPositionIdx === 'left' || _itemPositionIdx === 'next')
        _itemPositionIdx = itemPositionIdxRef.current + step

      if (_itemPositionIdx === 'right' || _itemPositionIdx === 'prev')
        _itemPositionIdx = itemPositionIdxRef.current - step

      if (circular || autoplay) {
        if (_itemPositionIdx < 0) {
          _itemPositionIdx = itemsPositions.length - 1
          animated = false
        }
        if (_itemPositionIdx >= itemsPositions.length) {
          _itemPositionIdx = 0
          animated = false
        }
      }

      _itemPositionIdx = Math.min(Math.max(0, _itemPositionIdx), itemsPositions.length - 1)

      // todo: improve this check -- to avoid unnecessary scrolls
      // if (itemPositionIdxRef.current === _itemPositionIdx) return

      setItemPositionIdx(_itemPositionIdx)

      const left = itemsPositions[_itemPositionIdx]

      const handleScrollStart = () => {
        onScrollStart?.(isResizing)
      }
      const handleScrollComplete = () => {
        if (!hovered.current) timedAutoplay?.wait()
        onScrollEnd?.(isResizing)
      }

      if (animated && !isResizing) {
        tlRef.current.clear().to(refContainer.current, {
          duration: speed / 1000,
          scrollTo: { x: left },
          ease: 'power1.inOut',
          onStart: () => {
            handleScrollStart()
          },
          onComplete: () => {
            handleScrollComplete()
          },
        })
      } else {
        tlRef.current.clear()
        handleScrollStart()
        container.scrollTo({ left, behavior: 'instant' })
        handleScrollComplete()
      }
    },
    [
      scrollSpeed,
      calculatePositions,
      step,
      circular,
      autoplay,
      onScrollStart,
      timedAutoplay,
      onScrollEnd,
    ],
  )

  const scrollToPrevIdx = useCallback(() => {
    scrollToIdx('prev', true)
  }, [scrollToIdx])

  const scrollToNextIdx = useCallback(() => {
    scrollToIdx('next', true)
  }, [scrollToIdx])

  const handleWindowResize = useCallback(() => {
    scrollToIdx()
  }, [scrollToIdx])
  useWindowSize(handleWindowResize)

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const fn = (() => {
        switch (e.key) {
          case 'ArrowRight':
            return scrollToNextIdx

          case 'ArrowLeft':
            return scrollToPrevIdx
        }
      })()

      if (fn) {
        e.preventDefault()
        fn()
      }
    },
    [scrollToNextIdx, scrollToPrevIdx],
  )

  usePageLoad(() => {
    calculatePositions()
    timedAutoplay?.wait()
  })

  const timedMouseLeave = useTimeout(
    !autoplay
      ? undefined
      : {
          callback: () => {
            tlRef.current.resume()
            timedAutoplay?.start()
          },
          delay: 2000,
        },
  )

  const timedMouseEnter = useTimeout(
    !autoplay
      ? undefined
      : {
          callback: () => {
            timedAutoplay?.cancel()
            tlRef.current.pause()
          },
          delay: 100,
        },
  )

  const handleMouseEnter = useCallback(() => {
    hovered.current = true
    timedMouseLeave?.cancel()
    timedMouseEnter?.wait()
  }, [timedMouseEnter, timedMouseLeave])

  const handleMouseLeave = useCallback(() => {
    hovered.current = false
    timedMouseEnter?.cancel()
    timedMouseLeave?.wait()
  }, [timedMouseEnter, timedMouseLeave])

  useEffect(() => {
    const container = refContainer.current
    if (!container) return

    container.addEventListener('keydown', handleKeyPress)
    // container.addEventListener('mouseenter', handleMouseEnter)
    // container.addEventListener('mouseleave', handleMouseLeave)
    return () => {
      container.removeEventListener('keydown', handleKeyPress)
      // container.removeEventListener('mouseenter', handleMouseEnter)
      // container.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [calculatePositions, handleKeyPress, handleMouseEnter, handleMouseLeave])

  return {
    carousel: refContainer,
    refCarousel,
    scrollToIdx,
    scrollToPrevIdx,
    scrollToNextIdx,
    itemPositionIdx,
    calculatePositions,
  }
}
